import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Admin, getAdminProfile } from '../services/adminApi.ts';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Sidebar = styled.div`
  width: 250px;
  background-color: #2c3e50;
  color: white;
  padding: 1.5rem 0;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  padding: 0 1.5rem 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const NavMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: 0.5rem;
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  display: block;
  padding: 0.75rem 1.5rem;
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  text-decoration: none;
  transition: all 0.2s;
  background-color: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const Content = styled.div`
  flex: 1;
  background-color: #f5f7fa;
`;

const Header = styled.header`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem 2rem;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
`;

const UserName = styled.span`
  margin-right: 1rem;
  font-weight: 500;
`;

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: #e53e3e;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AdminLayout: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    console.log('AdminLayout - Token exists:', !!token);
    
    if (!token) {
      console.log('AdminLayout - No token found, redirecting to login');
      navigate('/admin/login');
      return;
    }

    const fetchUserProfile = async () => {
      try {
        console.log('AdminLayout - Fetching user profile...');
        const data = await getAdminProfile();
        console.log('AdminLayout - Profile data:', data);
        
        setUser(data.profile);
        setUserType(data.userType);
        
        // No need to redirect based on user type anymore since we're using the same dashboard
      } catch (err) {
        console.error('AdminLayout - Error fetching user profile:', err);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        localStorage.removeItem('user_type');
        navigate('/admin/login');
      }
    };

    fetchUserProfile();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('user_type');
    navigate('/admin/login');
  };

  // Check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <LayoutContainer>
      <Sidebar>
        <Logo>Spin To Connect</Logo>
        <NavMenu>
          {userType === 'Admin' ? (
            // Admin navigation
            <>
              <NavItem>
                <NavLink to="/admin/dashboard" $active={isActive('/admin/dashboard')}>Dashboard</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/admin/users" $active={isActive('/admin/users')}>Users</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/admin/wheels" $active={isActive('/admin/wheels')}>Wheels</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/admin/business-users" $active={isActive('/admin/business-users')}>Business Users</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/admin/demo-requests" $active={isActive('/admin/demo-requests')}>Demo Requests</NavLink>
              </NavItem>
              {user.isSuperAdmin && (
                <NavItem>
                  <NavLink to="/admin/admins" $active={isActive('/admin/admins')}>Admins</NavLink>
                </NavItem>
              )}
              <NavItem>
                <NavLink to="/admin/settings" $active={isActive('/admin/settings')}>Settings</NavLink>
              </NavItem>
            </>
          ) : (
            // Business user navigation
            <>
              <NavItem>
                <NavLink to="/admin/dashboard" $active={isActive('/admin/dashboard')}>Dashboard</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/admin/wheels" $active={isActive('/admin/wheels')}>My Wheels</NavLink>
              </NavItem>
            </>
          )}
        </NavMenu>
      </Sidebar>
      <Content>
        <Header>
          <UserInfo>
            <UserName>{user.firstName} {user.lastName}</UserName>
            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          </UserInfo>
        </Header>
        <Outlet />
      </Content>
    </LayoutContainer>
  );
};

export default AdminLayout; 